import { Dispatch } from 'redux';
import ConnectionsController from '../controllers/ConnectionsController';
import commonHandler from '../errorHandlers/common-handler';
import { connectionErrorHandler } from '../errorHandlers/connections-handler';
import { integrationsHandler } from '../errorHandlers/integrations-handler';
import ConnectionModel from '../model/ConnectionModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class MailchimpConnectionsCRUDActions extends CrudActionContainer {
  retrieveWidgetConnectionsAction: string;
  updateVariableOptionsAction: string;

  constructor() {
    super('MAILCHIMP_CONNECTIONS');

    this.retrieveWidgetConnectionsAction = this.makeActionKey(
      'RETRIVE_WIDGET_CONNECTIONS',
    );
    this.updateVariableOptionsAction = this.makeActionKey(
      'UPDATE_VARIABLE_OPTIONS',
    );
  }

  create(facebookPageId: number, connection: Partial<ConnectionModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      try {
        const response: ConnectionModel =
          await ConnectionsController.postWebhook(facebookPageId, connection);
        dispatch(this.retrieved(response));

        return response;
      } catch (error) {
        return commonHandler(dispatch)(
          error,
          integrationsHandler(dispatch, this),
        );
      }
    };
  }

  retrieveById(facebookPageId: number, connectionId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));

      try {
        const response = await ConnectionsController.getById(
          facebookPageId,
          connectionId,
        );
        dispatch(this.retrieved(response));

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  retrieveAll(
    facebookPageId: number,
    params?: {
      pageNumber?: number;
      pageSize?: number;
      query?: string;
      type?: string;
    },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));

      try {
        const response = await ConnectionsController.getAll(
          facebookPageId,
          params,
        );
        dispatch(this.retrievedAll(response));

        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      }
    };
  }

  deleteConnection(facebookPageId: number, connection: ConnectionModel) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteAction));

      try {
        const response = await ConnectionsController.deleteWebhook(
          facebookPageId,
          connection.id,
        );
        await dispatch(this.deleteLocal(connection));
        return response;
      } catch (e) {
        connectionErrorHandler(dispatch)(e, facebookPageId);
      }
    };
  }

  update(facebookPageId: number, connection: Partial<ConnectionModel>) {
    return async (dispatch: Dispatch<StoreState>, getState) => {
      const { mailchimpConnections } = getState();

      try {
        const connectionInstance = mailchimpConnections.entities.find(
          item => item.id === connection.id,
        );
        const nextConnection = {
          ...connectionInstance,
          ...connection,
        };

        dispatch(this.busy(this.createAction));
        const response = await ConnectionsController.update(
          facebookPageId,
          nextConnection,
        );
        dispatch(this.retrieved(nextConnection));

        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrievedWidgetConnections(connections: ConnectionModel[]) {
    return {
      type: this.retrieveWidgetConnectionsAction,
      connections,
    };
  }

  updateVariableOptions(options) {
    return {
      type: this.updateVariableOptionsAction,
      options,
    };
  }
}

const mailchimpConnectionsCRUDActions = new MailchimpConnectionsCRUDActions();

export default mailchimpConnectionsCRUDActions;

export const useMailchimpConnectionCRUDActions = getActionHookBuilder(
  mailchimpConnectionsCRUDActions,
  ['create', 'deleteConnection', 'retrieveAll', 'retrieveById', 'update'],
);
